// ASK LALA UPLOADS (U-batch) — **ONE IMPLEMENTATION, BOTH DOORS.**
//
// Lala is one mind behind two doors (the drawer and the Ask Lala page), so attaching is ONE
// implementation both of them use — the same law as `NameFields`, the `SubjectPicker` and
// `DrawerTab`. All three of those live in `src/components/`, and this was the odd one out: it sat
// inside an eleven-thousand-line file where nothing could import it, which is the only reason its
// FOUR refusal states have never been driven by anything.
//
// >>> AND THOSE FOUR STATES ARE WHAT MAKE IT WORTH REACHING (R18). <<< A member who is signed out, a
// member whose files are too large, a member whose file the SERVER'S OWN SNIFFER refused by name, and
// a request that never arrived are four different facts and she is told which — the per-file
// rejection sentence in particular is the server's own words rather than a guess composed here
// (R83). Collapsing any two of them is a member staring at "that could not be attached" with no idea
// whether to sign in, shrink the file or try again.
//
// It is in `src/lib` beside `shelf-overflow.ts`, which is the precedent for a HOOK here: the
// import-free rule in this folder is for the PURE laws a `.mjs` gate loads and runs, and a hook is
// React by definition. What it buys is the IMPORT — `TierCards` / `MicButton` / `SignInConfirmation`'s
// own class of extraction.
//
// THE BYTES ARE READ AS BASE64 AND THE SERVER RE-CHECKS THE REAL TYPE FROM THEM, whatever the name or
// the browser claims. R15's own law is that the picker's `accept` and the sniffer's accepted set are
// ONE declaration, so nothing here decides what a file is.
import { useState } from "react";
import type { LalaAttachment } from "../components/MessageAttachments";

export function useAttachments() {
  const [pending, setPending] = useState<LalaAttachment[]>([]);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");

  async function attach(files: FileList | File[]) {
    const list = Array.from(files);
    if (!list.length) return;
    setUploading(true);
    setError("");
    try {
      const payload = await Promise.all(list.map(async (f) => ({
        filename: f.name,
        // Read as base64 for the JSON upload. The server re-checks the real type from the
        // bytes regardless of what the name or the browser claims.
        data: await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(String(reader.result).split(",")[1] || "");
          reader.onerror = () => reject(new Error("read failed"));
          reader.readAsDataURL(f);
        }),
      })));
      const res = await fetch("/api/lala/attachments", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ files: payload }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.status === 401) { setError("Sign in to attach a file."); return; }
      if (res.status === 413) { setError(data?.message || "Those files are too large."); return; }
      if (Array.isArray(data?.attachments) && data.attachments.length) {
        setPending((p) => [...p, ...data.attachments]);
      }
      // Say plainly what was refused and why, per file.
      if (Array.isArray(data?.rejected) && data.rejected.length) {
        setError(data.rejected.map((r: { filename: string; message: string }) => `${r.filename}: ${r.message}`).join(" "));
      } else if (!data?.ok && data?.message) {
        setError(data.message);
      }
    } catch {
      setError("That file could not be attached. Please try again.");
    } finally {
      setUploading(false);
    }
  }
  const remove = (id: string) => setPending((p) => p.filter((a) => a.id !== id));
  const clear = () => { setPending([]); setError(""); };
  return { pending, uploading, error, attach, remove, clear, ids: pending.map((a) => a.id) };
}
